import axios from "axios";
import { api } from "../config";

axios.defaults.baseURL = api.API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.withCredentials = true;

// Al cargar el módulo, restablecer Authorization desde localStorage para que el
// primer render tras F5 ya salga autenticado sin depender de useEffect en hijos.
try {
  const stored = localStorage.getItem("authUser");
  if (stored) {
    const parsed = JSON.parse(stored);
    const bootToken = parsed?.access_token || parsed?.token;
    if (bootToken) {
      axios.defaults.headers.common["Authorization"] = "Bearer " + bootToken;
    }
  }
} catch (_) {
  /* localStorage corrupto: se ignorará y el interceptor 401 hará refresh */
}

axios.interceptors.request.use((config) => {
  const wid = localStorage.getItem("kronaly_workspace_id");
  if (wid) {
    config.headers = config.headers || {};
    config.headers["X-Workspace-Id"] = wid;
  }
  return config;
});

let isRefreshing = false;
let refreshWaiters = [];

const subscribeTokenRefresh = () =>
  new Promise((resolve, reject) => {
    refreshWaiters.push({ resolve, reject });
  });

const flushRefreshWaiters = (error, token) => {
  const waiters = refreshWaiters;
  refreshWaiters = [];
  waiters.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
};

const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

const clearAuthorization = () => {
  delete axios.defaults.headers.common["Authorization"];
};

/**
 * Error enriquecido con metadatos del API (`code`, `status`).
 *
 * Compatibilidad: muchos call sites históricos hacen `typeof e === "string" ? e : e?.message || "fallback"`.
 * Para no perder el mensaje real, esos sitios deben pasarse a leer `e?.message` como
 * fallback. Network errors (sin respuesta HTTP) siguen rechazándose como string para
 * seguir funcionando con el patrón antiguo.
 */
class ApiError extends Error {
  constructor(message, { code = null, status = null, details = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function rejectWithMessage(error) {
  const base = axios.defaults.baseURL || "(sin REACT_APP_API_URL)";

  // Sin respuesta HTTP: API caída, puerto mal, CORS bloqueado en preflight, etc.
  if (!error.response) {
    const isNetwork =
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      (!!error.request && !error.response);
    if (isNetwork) {
      const proxyHint =
        String(base).startsWith("/") || String(base).startsWith("http://localhost:3000")
          ? " Revisa `src/setupProxy.js` y `REACT_APP_PROXY_TARGET` (en Docker: `host.docker.internal:8080`)."
          : " Revisa `REACT_APP_API_URL` en `.env` y reinicia `npm start`.";
      return Promise.reject(
        `No hay conexión con la API (${base}).` +
          proxyHint +
          " Levanta kronaly-core (Docker en :8080 o `php artisan serve` en :8000 con URL explícita en .env)."
      );
    }
  }

  const status = error.response ? error.response.status : null;
  const apiError = error.response?.data?.error;
  const apiCode = apiError?.code || null;
  const apiDetails = apiError?.details || null;

  let message;
  switch (status) {
    case 500:
      message = "Error interno del servidor";
      break;
    case 401:
      message = "No autenticado";
      break;
    case 403:
      message = apiError?.message || "Acceso denegado";
      break;
    case 404:
      message = apiError?.message || "Recurso no encontrado";
      break;
    case 422: {
      if (apiDetails && typeof apiDetails === "object" && !Array.isArray(apiDetails)) {
        const first = Object.keys(apiDetails)[0];
        const arr = first != null ? apiDetails[first] : null;
        message = Array.isArray(arr) ? arr[0] : String(arr || apiError?.message);
      } else {
        message = apiError?.message || "Error de validación";
      }
      break;
    }
    default:
      message = apiError?.message || error.message || String(error);
  }

  return Promise.reject(
    new ApiError(message, { code: apiCode, status, details: apiDetails }),
  );
}

axios.interceptors.response.use(
  function (response) {
    return response.data !== undefined ? response.data : response;
  },
  async function (error) {
    const originalRequest = error.config;
    const status = error.response ? error.response.status : null;
    const reqUrl = String(originalRequest?.url || "");
    const isAuthPath =
      reqUrl.includes("/auth/login") ||
      reqUrl.includes("/auth/refresh") ||
      reqUrl.includes("/auth/register") ||
      reqUrl.includes("/auth/verify-registration") ||
      reqUrl.includes("/auth/resend-registration-verification") ||
      reqUrl.includes("/auth/impersonate") ||
      reqUrl.includes("/auth/logout");

    // Recuperación de `kronaly_workspace_id` huérfano (típico tras dump
    // anonimizado en dev: el ID cacheado ya no existe en la nueva BD). Se
    // descarta y se recarga la SPA una sola vez; el WorkspaceSwitcher re-ancla
    // al primer workspace válido del usuario al volver a arrancar.
    const apiErr = error.response?.data?.error;
    const apiCode = apiErr?.code || null;
    const isWorkspaceMismatch =
      (status === 403 && apiCode === "FORBIDDEN_WORKSPACE") ||
      (status === 400 &&
        (apiCode === "MISSING_WORKSPACE_HEADER" ||
          apiCode === "INVALID_WORKSPACE_ID"));
    if (isWorkspaceMismatch) {
      const wid = localStorage.getItem("kronaly_workspace_id");
      const alreadyRecovered = sessionStorage.getItem("kronaly_ws_recovered");
      if (wid && !alreadyRecovered) {
        localStorage.removeItem("kronaly_workspace_id");
        sessionStorage.setItem("kronaly_ws_recovered", "1");
        window.location.reload();
        return new Promise(() => {}); // bloquea hasta que el reload tome el control
      }
    }

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthPath
    ) {
      if (isRefreshing) {
        try {
          const access = await subscribeTokenRefresh();
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = "Bearer " + access;
          return axios(originalRequest);
        } catch (e) {
          return rejectWithMessage(
            e?.response ? e : { response: { status: 401, data: {} } }
          );
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const r = await axios.post(
          `${axios.defaults.baseURL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const inner = r?.data ?? r;
        const access = inner.access_token;
        if (!access) {
          throw new Error("Sin access_token en refresh");
        }

        // Impersonación activa: el refresh nos devuelve el access token del
        // ACTOR (su cookie sigue siendo la fuente de verdad). Lo reconvertimos
        // al access token del usuario impersonado para que la sesión "como X"
        // sobreviva a la expiración del token de 15 min. Si la reconversión
        // falla (allowlist revocada, objetivo desactivado…), caemos a la sesión
        // del actor limpiando el flag.
        let effectiveAccess = access;
        const impRaw = localStorage.getItem("kronaly_impersonation");
        if (impRaw) {
          try {
            const imp = JSON.parse(impRaw);
            const ir = await axios.post(
              `${axios.defaults.baseURL}/auth/impersonate`,
              { email: imp.email },
              {
                withCredentials: true,
                headers: { Authorization: "Bearer " + access },
              }
            );
            const innerImp = ir?.data ?? ir;
            if (innerImp?.access_token) effectiveAccess = innerImp.access_token;
          } catch (_) {
            localStorage.removeItem("kronaly_impersonation");
          }
        }

        setAuthorization(effectiveAccess);
        const raw = localStorage.getItem("authUser");
        if (raw) {
          const u = JSON.parse(raw);
          u.access_token = effectiveAccess;
          localStorage.setItem("authUser", JSON.stringify(u));
        }
        flushRefreshWaiters(null, effectiveAccess);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = "Bearer " + effectiveAccess;
        return axios(originalRequest);
      } catch (e) {
        flushRefreshWaiters(e, null);
        localStorage.removeItem("authUser");
        localStorage.removeItem("kronaly_workspace_id");
        clearAuthorization();
        // La sesión está realmente cerrada (refresh 401). `useProfile`/`AuthProtected`
        // ya decidieron renderizar a partir del localStorage que había al montar, así
        // que limpiarlo no basta: hay que forzar la navegación al login o la SPA se
        // queda con la UI a medias y todas las llamadas en 401. Evitamos el bucle si
        // ya estamos en una ruta de auth.
        const path = window.location.pathname;
        const onAuthRoute =
          path === "/login" ||
          path === "/logout" ||
          path.startsWith("/register") ||
          path.startsWith("/verify") ||
          // La página pública de invitación/activación llama a /me para
          // detectar si hay sesión; un 401 (token viejo del que invitó)
          // debe caer al flujo anónimo, NO expulsar al invitado al login.
          path.startsWith("/invitations");
        if (!onAuthRoute) {
          window.location.assign("/login");
        }
        return rejectWithMessage(
          e.response ? e : { response: { status: 401, data: {} } }
        );
      } finally {
        isRefreshing = false;
      }
    }

    return rejectWithMessage(error);
  }
);

class APIClient {
  get = (url, params) => {
    if (!params) return axios.get(url);
    const parts = [];
    Object.keys(params).forEach((key) => {
      const v = params[key];
      if (v === null || v === undefined) return;
      // Arrays se serializan como `key[]=val&key[]=val` (formato que Laravel
      // espera para reglas de validación `array`).
      if (Array.isArray(v)) {
        v.forEach((item) => {
          if (item === null || item === undefined) return;
          parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`);
        });
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      }
    });
    const queryString = parts.join("&");
    return axios.get(queryString ? `${url}?${queryString}` : url);
  };

  create = (url, data) => axios.post(url, data);

  update = (url, data) => axios.patch(url, data);

  put = (url, data) => axios.put(url, data);

  delete = (url, config) => axios.delete(url, { ...config });
}

const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  if (!user) return null;
  return JSON.parse(user);
};

export { APIClient, ApiError, setAuthorization, clearAuthorization, getLoggedinUser };
